import { useFlag } from '@/components/posthog-provider';
import { Button } from '@/components/ui/button';
import {
  ServiceTagsDialog,
  ServiceTagsEditor,
} from '@/features/admin-terminal/components/stock-footage/service-tags-dialog';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { OrganizationServiceStockClips } from '@borradh-workspace/api-client/types';
import type { ServiceFootageChoices } from '@borradh-workspace/contracts';
import { organizationServiceStockClipsResponseSchema } from '@borradh-workspace/contracts';
import { STOCK_FOOTAGE_MATCHING_NEW_FLAG } from '@borradh-workspace/labels';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ServiceFootageFields,
  defaultServiceFootageChoices,
} from './service-footage-fields';

export function ServiceFootageRequirements({
  serviceId,
  serviceName,
  organizationId,
  inline = false,
  disabled = false,
}: {
  serviceId: string;
  serviceName?: string;
  organizationId: string;
  inline?: boolean;
  disabled?: boolean;
}) {
  const enabled = useFlag(STOCK_FOOTAGE_MATCHING_NEW_FLAG);
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: queryKeys.serviceFootage.organization(organizationId),
    enabled: enabled && (open || inline),
    queryFn: () =>
      apiClient.get<OrganizationServiceStockClips>(
        'organization-services/footage',
        { schema: organizationServiceStockClipsResponseSchema }
      ),
  });
  if (!enabled) return null;
  const service = query.data?.services.find((s) => s.id === serviceId) ?? null;
  return (
    <div className="space-y-2">
      {!inline ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
          disabled={open && query.isLoading}
        >
          Footage requirements
        </Button>
      ) : null}
      {(open || inline) && query.isLoading ? (
        <p role="status" className="text-sm text-muted-foreground">
          Loading footage requirements…
        </p>
      ) : null}
      {(open || inline) && query.isError ? (
        <div role="alert" className="text-sm text-destructive">
          Could not load footage requirements.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void query.refetch()}
          >
            Retry
          </button>
        </div>
      ) : null}
      {(open || inline) && query.isSuccess && !service ? (
        <p role="alert">This service is unavailable.</p>
      ) : null}
      {inline && service ? (
        <ServiceTagsEditor
          key={JSON.stringify([
            service.id,
            service.specSource,
            service.techniqueSlug,
            service.agents,
            service.regions,
            service.footageActions,
            service.footageOtherFields,
            service.treatments,
            service.suggestions,
          ])}
          serviceName={serviceName}
          clinic
          embedded
          disabled={disabled}
          organizationId={organizationId}
          service={service}
          catalogue={query.data?.catalogue ?? []}
          techniques={query.data?.techniques ?? []}
          onClose={() => {}}
        />
      ) : null}
      {!inline ? (
        <ServiceTagsDialog
          clinic
          organizationId={organizationId}
          service={open ? service : null}
          catalogue={query.data?.catalogue ?? []}
          techniques={query.data?.techniques ?? []}
          onOpenChange={setOpen}
        />
      ) : null}
    </div>
  );
}

export function CreateServiceFootageChoices({
  organizationId,
  serviceName,
  value,
  onChange,
  disabled,
}: {
  organizationId?: string;
  serviceName?: string;
  value?: ServiceFootageChoices;
  onChange: (value: ServiceFootageChoices) => void;
  disabled?: boolean;
}) {
  const enabled = useFlag(STOCK_FOOTAGE_MATCHING_NEW_FLAG);
  const query = useQuery({
    queryKey: queryKeys.serviceFootage.organization(organizationId),
    enabled: enabled && !!organizationId,
    queryFn: () =>
      apiClient.get<OrganizationServiceStockClips>(
        'organization-services/footage',
        { schema: organizationServiceStockClipsResponseSchema }
      ),
  });
  if (!enabled) return null;
  if (query.isLoading) return <p role="status">Loading footage choices…</p>;
  if (query.isError)
    return (
      <p role="alert">
        Could not load footage choices. You can still save the service without
        them.
      </p>
    );
  return (
    <ServiceFootageFields
      organizationId={organizationId}
      serviceName={serviceName}
      value={value ?? defaultServiceFootageChoices()}
      onChange={onChange}
      catalogue={query.data?.catalogue ?? []}
      techniques={query.data?.techniques ?? []}
      disabled={disabled}
      idPrefix="create-service-footage"
    />
  );
}
